/**
 * "Nuovo gruppo" (da Nuova chat): nome + partecipanti (Lead/Clienti o numeri).
 * Solo numeri Standard (WAHA): sui numeri Ufficiale WhatsApp non permette i gruppi.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, X, Loader2, Users } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { createGroup } from '@/lib/services/whatsapp/wahaGroups';
import { iconTip } from '@/components/messages/ChatTooltip';
import { ParticipantPicker, type PickedPerson } from './ParticipantPicker';

interface Props {
  session: string;
  configId: string;
  /** Etichetta del numero da cui si crea (se l'utente ne ha piu' d'uno). */
  numberLabel?: string | null;
  onBack: () => void;
  onClose: () => void;
}

export function NewGroupModal({ session, configId, numberLabel, onBack, onClose }: Props) {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<PickedPerson[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const canCreate = name.trim().length > 0 && picked.length > 0 && !busy;

  const create = async () => {
    if (!canCreate) return;
    setBusy(true);
    try {
      const subject = name.trim();
      const id = await createGroup(session, subject, picked.map((p) => p.digits));
      const store = useWhatsAppChatStore.getState();
      if (id && store.wahaConfig?.configId === configId) {
        store.addChatToStore({ id, name: subject, isGroup: true, pinned: false, archived: false, participantsCount: picked.length + 1 });
        store.selectChat(id);
        useWhatsAppDrawerStore.getState().openChannel('whatsapp');
        toast.success(`Gruppo "${subject}" creato`);
      } else {
        toast.success(`Gruppo "${subject}" creato: lo trovi nell'elenco chat${numberLabel ? ` di ${numberLabel}` : ''} tra pochi istanti`);
        void store.refreshChats?.();
      }
      onClose();
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Creazione del gruppo non riuscita. Riprova tra poco.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-start justify-center bg-black/30 px-4 pt-[12vh]" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Nuovo gruppo" className="w-full max-w-md rounded-xl bg-white dark:bg-[#252528] shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <button type="button" onClick={onBack} disabled={busy} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10" {...iconTip('Torna a Nuova chat')}>
            <ArrowLeft className="w-4 h-4 text-[#787774]" />
          </button>
          <h3 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Nuovo gruppo WhatsApp</h3>
          <button type="button" onClick={onClose} disabled={busy} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10" {...iconTip('Chiudi')}>
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        <div className="px-4 pb-4 space-y-3">
          {numberLabel && <p className="text-xs text-[#787774]">Il gruppo sarà creato dal numero {numberLabel}, che ne sarà amministratore.</p>}
          <label className="block">
            <span className="text-xs text-[#787774]">Nome del gruppo</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} disabled={busy} autoFocus
              placeholder="Es. Clienti evento di ottobre"
              className="mt-1 w-full h-9 px-3 rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 bg-white dark:bg-white/5 text-sm text-[#37352F] dark:text-white placeholder:text-[#787774] focus:outline-none focus:border-[#2383E2]" />
          </label>
          <div>
            <span className="text-xs text-[#787774]">Partecipanti</span>
            <div className="mt-1"><ParticipantPicker selected={picked} onChange={setPicked} disabled={busy} /></div>
          </div>
          <p className="text-[11px] text-[#787774]">Chi ha limitato con le impostazioni di privacy chi può aggiungerlo ai gruppi non verrà aggiunto: potrai mandargli il link d'invito da Info gruppo.</p>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#37352F] dark:text-white">Annulla</button>
            <button type="button" onClick={() => void create()} disabled={!canCreate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-[#2383E2] text-white disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}Crea gruppo
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
